import { getIn } from "formik";
import {
  FGNumberInput,
  FGTextInput,
  FieldGroup,
  useFGContext,
} from "nsitools-react";
import * as React from "react";
import { ETLCodes } from "../../locales";
import { CountrySelect } from "../selects/CountrySelect";
import { CitySelect } from "../selects/CitySelect";
import { PostalCodeSelect } from "../selects/PostalCodeSelect";
import { useCommonHooks, useCountryRef } from "../../hooks";
import { Classes } from "@blueprintjs/core";

export interface IFGAddressFieldsProps {
  addressObjectKey: string;
  required: boolean;
  customForBelgium: boolean;
  disabled?: boolean;
  infoText?: string;
}

export const FGAddressFields: React.FunctionComponent<
  IFGAddressFieldsProps
> = ({
  addressObjectKey,
  required,
  customForBelgium,
  disabled = false,
  infoText,
}) => {
  const { belgiumId } = useCountryRef();
  const { t } = useCommonHooks();
  const { formik, loading } = useFGContext();
  const isBelgium = React.useMemo(() => {
    if (!loading) {
      return (
        +getIn(formik.values, addressObjectKey + ".countryId") === +belgiumId
      );
    }
    return false;
  }, [addressObjectKey, belgiumId, formik.values, loading]);

  const postalCode = React.useMemo(
    () => getIn(formik.values, addressObjectKey + ".zipCode"),
    [addressObjectKey, formik.values]
  );

  return (
    <>
      {infoText && (
        <div
          style={{
            color: "#5f6b7c",
            fontSize: "12px",
            marginBottom: "5px",
          }}
        >
          {infoText}
        </div>
      )}
      <FieldGroup columns={[6, 3, 3]}>
        <FGTextInput
          name={addressObjectKey + ".street"}
          label={t(ETLCodes.Street)}
          maxLength={255}
          requiredMark={required}
          disabled={disabled}
        />
        <FGTextInput
          name={addressObjectKey + ".number"}
          label={t(ETLCodes.Number)}
          requiredMark={required}
          disabled={disabled}
        />
        <FGTextInput
          name={addressObjectKey + ".postalBox"}
          label={t(ETLCodes.PostBox)}
          maxLength={10}
          disabled={disabled}
        />
      </FieldGroup>
      <FieldGroup columns={2}>
        <CountrySelect
          name={addressObjectKey + ".countryId"}
          label={t(ETLCodes.Country)}
          requiredMark={required}
          disabled={disabled}
        />
      </FieldGroup>
      {isBelgium && customForBelgium ? (
        <FieldGroup columns={2}>
          <PostalCodeSelect
            name={addressObjectKey + ".zipCode"}
            requiredMark={required}
            disabled={disabled}
          />
          <CitySelect
            name={addressObjectKey + ".city"}
            postCode={postalCode}
            disabled={disabled}
          />
        </FieldGroup>
      ) : (
        <FieldGroup columns={[4, 8]}>
          <FGTextInput
            name={addressObjectKey + ".zipCode"}
            label={t(ETLCodes.ZipCode)}
            requiredMark={required}
            disabled={disabled}
          />
          <FGTextInput
            name={addressObjectKey + ".city"}
            label={t(ETLCodes.City)}
            requiredMark={required}
            disabled={disabled}
          />
        </FieldGroup>
      )}
    </>
  );
};
